import type { CSSProperties, MouseEvent } from 'react'
import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { FlightsClassTypes, isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { OneWayFlightSaleLabel } from './MaldivesCharterOneWayFlightSaleLabel'
import { shouldShowReturnFareFreeCalendarLabel } from './returnFareFreeCalendarVisibility'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import InlineIcon from 'components/utils/InlineIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import TextButton from 'LuxKit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Group from 'components/utils/Group'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import useResizeObserver from 'hooks/useResizeObserver'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCalendarItemData } from '../hooks/useCalendarItemData'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import { useSeatEligiblePassengers } from '../hooks/utils'
import { useCharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import CharterRewardGreatValueBadge from 'components/OfferPage/Filters/CharterFlight/CharterRewardGreatValueBadge'
import Divider from 'LuxKit/Divider'
import CharterCabinFeatureList, { getCharterCabinFeatureCount } from 'components/Flights/CharterFlights/CharterCabinFeatureList'
import { computeFareClassCellPrice } from './maldivesCharterTripOptionsTablePricing'
import { getRewardPreviewRate } from 'lib/maldivesCharter/classicRewardPricing'

/** Number of inclusion rows visible by default before the user taps "Show more" — matches the
 *  previous dynamic-feature-list behaviour. Shared across all cards in the row via the parent's
 *  `extraFeaturesExpanded` state so they expand together. */
const COLLAPSED_INCLUSIONS_COUNT = 2

const CardWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  align-self: stretch;
`

const CardPane = styled(Pane)<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: ${rem(12)};
  min-height: 0;
  min-width: ${rem(200)};
  max-width: ${rem(240)};
  border: 1px solid ${props => props.$selected
    ? props.theme.palette.neutral.default.one
    : props.theme.palette.neutral.default.five};
`

/** Height follows cabin title + optional loading row only (no fixed band, so no empty strip under the title). */
const CardHeaderSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
`

const CardMainColumn = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`

/** Grows when fare details are closed so the price / CTA stack sits on the bottom of the card. */
const FareCardMainLeadingSpacer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`

/**
 * Price / CTA / "View details" stack (fare inclusions render as siblings after `CardMainColumn` so they do not
 * shrink this column and shift the stack when expanded).
 */
const PreFareDetailsBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

/** Keeps the fare-details control above adjacent full-width controls so taps aren't swallowed. */
const FareDetailsToggleSlot = styled.div`
  position: relative;
  z-index: 1;
`

export interface MaldivesCharterTripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  available: boolean
}

interface Props {
  optionFareClass: FareClassWithReward
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  oneWayBundle?: boolean
  isSelectedFare: boolean
  tripOption: MaldivesCharterTripOption
  onPickFare: (fareClass: FareClassWithReward, option: MaldivesCharterTripOption) => void
  /**
   * When set, the primary "Selected" control is clickable and clears the chosen trip dates
   * (same control as "Select fare", toggled on when this cabin is selected for the row).
   */
  onUnselectSelectedFare?: () => void
  fareDetailsExpanded: boolean
  /** Shared across all cards in the row so toggling on one expands the inclusions list on every card. */
  extraFeaturesExpanded: boolean
  onToggleFareDetails: () => void
  onToggleExtraFeatures: () => void
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
}

function MaldivesCharterFareCarouselOption(props: Props) {
  const {
    optionFareClass,
    checkIn,
    checkOut,
    nights,
    rooms,
    priceMode,
    luxPlusEnabled,
    oneWayBundle,
    isSelectedFare,
    tripOption,
    onPickFare,
    onUnselectSelectedFare,
    fareDetailsExpanded,
    extraFeaturesExpanded,
    onToggleFareDetails,
    onToggleExtraFeatures,
    hotelPriceByCabinAndDate,
  } = props

  const intl = useIntl()

  const cardMainColumnRef = useRef<HTMLDivElement>(null)
  const collapsedMainColumnHeightRef = useRef<number | null>(null)
  const [collapsedMainColumnHeightPx, setCollapsedMainColumnHeightPx] = useState<number | null>(null)

  const readCollapsedMainColumnHeight = useCallback(() => {
    const el = cardMainColumnRef.current
    if (!el) {
      return
    }
    const next = Math.ceil(el.getBoundingClientRect().height)
    collapsedMainColumnHeightRef.current = next
    setCollapsedMainColumnHeightPx(next)
  }, [])

  useResizeObserver(
    cardMainColumnRef,
    useCallback(() => {
      if (fareDetailsExpanded) {
        return
      }
      readCollapsedMainColumnHeight()
    }, [fareDetailsExpanded, readCollapsedMainColumnHeight]),
  )

  useLayoutEffect(() => {
    if (fareDetailsExpanded) {
      return
    }
    readCollapsedMainColumnHeight()
  }, [fareDetailsExpanded, readCollapsedMainColumnHeight])

  useLayoutEffect(() => {
    if (!fareDetailsExpanded || collapsedMainColumnHeightPx != null) {
      return
    }
    const refH = collapsedMainColumnHeightRef.current
    if (refH != null) {
      setCollapsedMainColumnHeightPx(refH)
    }
  }, [fareDetailsExpanded, collapsedMainColumnHeightPx])

  const baseFareClass = toBaseFareClass(optionFareClass)

  const bundleData = useCalendarItemData({
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass: baseFareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
  })

  // Override hotel price with cabin-aware data when available
  const cabinHotel = hotelPriceByCabinAndDate?.[baseFareClass]?.[checkIn]
  const cabinAwareHotelPrice = cabinHotel?.hotelPrice ?? bundleData.hotelPrice
  const cabinAwareHotelMemberPrice = cabinHotel?.hotelMemberPrice ?? bundleData.hotelMemberPrice

  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)

  const isRewardFare = isRewardFareClass(optionFareClass)
  const showReturnFareFree = luxPlusEnabled && !isRewardFare && shouldShowReturnFareFreeCalendarLabel({
    hasReturnFareFree: bundleData.hasReturnFareFree,
    isEconomy: baseFareClass === FlightsClassTypes.ECONOMY,
  })
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardLockedForLogin = isRewardFare && !memberId
  const rewardLockedForEnroll = isRewardFare && !!memberId && !hasLoyaltyAccount
  const rewardLocked = rewardLockedForLogin || rewardLockedForEnroll

  const { getLegResult, getJourneysForCabinDate } = useCharterRewardAvailabilityContext()

  const rewardCellPrice = useMemo(() => {
    if (!isRewardFare) return undefined
    const cabinJourneys = getJourneysForCabinDate(toBaseFareClass(optionFareClass), checkIn)
    return computeFareClassCellPrice({
      fareClass: optionFareClass,
      hotelPrice: cabinAwareHotelPrice,
      hotelMemberPrice: cabinAwareHotelMemberPrice,
      departingFlight: cabinJourneys?.departing,
      returningFlight: cabinJourneys?.returning,
      allowIncompleteJourneys: !!oneWayBundle,
      seatEligiblePassengers,
      getLegResult,
    })
  }, [isRewardFare, optionFareClass, getJourneysForCabinDate, checkIn, cabinAwareHotelPrice, cabinAwareHotelMemberPrice, oneWayBundle, seatEligiblePassengers, getLegResult])

  /**
   * Per-cabin cash availability + price, computed the same way the desktop trip-options table
   * does ({@link computeFareClassCellPrice}). `bundleData.available` from `useCalendarItemData`
   * is too loose — it can be true even when the specific cabin (e.g. Economy Plus) has no
   * seats for the chosen dates — so we use this stricter per-journey signal as the cash
   * availability gate. `cashTotal === undefined` ⇒ "Unavailable".
   */
  const cashCellPrice = useMemo(() => {
    if (isRewardFare) return undefined
    const cabinJourneys = getJourneysForCabinDate(toBaseFareClass(optionFareClass), checkIn)
    return computeFareClassCellPrice({
      fareClass: optionFareClass,
      hotelPrice: cabinAwareHotelPrice,
      hotelMemberPrice: cabinAwareHotelMemberPrice,
      departingFlight: cabinJourneys?.departing,
      returningFlight: cabinJourneys?.returning,
      allowIncompleteJourneys: !!oneWayBundle,
      seatEligiblePassengers,
      getLegResult,
    })
  }, [isRewardFare, optionFareClass, getJourneysForCabinDate, checkIn, cabinAwareHotelPrice, cabinAwareHotelMemberPrice, oneWayBundle, seatEligiblePassengers, getLegResult])

  // Use cabin-aware hotel + flight for the cash display when per-cabin hotel data exists
  const displayTotal = useMemo(() => {
    if (cabinHotel && bundleData.flightPrice != null) {
      const hotel = luxPlusEnabled ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0) : (cabinHotel.hotelPrice || 0)
      return hotel + bundleData.flightPrice
    }
    if (luxPlusEnabled && bundleData.memberTotalPrice) {
      return bundleData.memberTotalPrice
    }
    return bundleData.totalPrice
  }, [cabinHotel, bundleData.flightPrice, bundleData.memberTotalPrice, bundleData.totalPrice, luxPlusEnabled])

  const displayPerPerson = useMemo(() => {
    const totalPassengers = Math.max(1, seatEligiblePassengers)
    if (cabinHotel && bundleData.flightPrice != null) {
      const hotel = luxPlusEnabled ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0) : (cabinHotel.hotelPrice || 0)
      return (hotel + bundleData.flightPrice) / totalPassengers
    }
    if (luxPlusEnabled && bundleData.memberPricePerPerson) {
      return bundleData.memberPricePerPerson
    }
    return bundleData.pricePerPerson
  }, [cabinHotel, bundleData.flightPrice, bundleData.memberPricePerPerson, bundleData.pricePerPerson, luxPlusEnabled, seatEligiblePassengers])

  const displayAmount = priceMode === 'perPerson' ? displayPerPerson : displayTotal

  const showModal = useContext(ModalContext)

  const cabinLabel = getCharterFareCabinLabel(intl, isRewardFare ? toBaseFareClass(optionFareClass) : optionFareClass)
  const rewardsLabel = intl.formatMessage(
    {
      id: 'ARQye1XQ7EdWBVnFNd_f',
      defaultMessage: '{loyaltyProgramName} Rewards',
    },
    { loyaltyProgramName: getLoyaltyProgramDisplayName() },
  )

  const selectFareLabel = intl.formatMessage({
    id: 'dTkIumM2bXfSy6kUf7f9',
    defaultMessage: 'Select fare',
  })

  const selectedFareButtonLabel = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
  })

  const loginToViewLabel = intl.formatMessage({
    id: 'zp0O9z2ci6Dk9LPEDw0u',
    defaultMessage: 'Log in to view',
  })
  const enrollLabel = intl.formatMessage({
    id: 'u1AVi50L43eqeNElJ9DP',
    defaultMessage: 'Join {loyaltyProgramName}',
  }, { loyaltyProgramName: getLoyaltyProgramDisplayName() })

  const unselectSelectedFareAriaLabel = intl.formatMessage({
    id: '8scoatopYk5NhrisNiaf',
    defaultMessage: 'Clear trip dates and turn off this fare selection',
  })

  const loadingLabel = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
  })

  const perPersonSuffix = intl.formatMessage({
    id: 'ew2ARy7ro3tFSM4inpHN',
    defaultMessage: '/person',
  })

  const totalSuffix = intl.formatMessage({
    id: 'j--PuUYLTgWUxAxROh0Z',
    defaultMessage: 'total',
  })

  const fromLabel = intl.formatMessage({
    id: 'LYZ7jXNWiO9WbF074c5M',
    defaultMessage: 'From',
  })

  const unavailableLabel = intl.formatMessage({
    id: 'lbS33CL-vQm3bmd7lueV',
    defaultMessage: 'Sold out!',
  })

  const showDetailsLabel = intl.formatMessage({
    id: 'ILN5aY-U2C28L_ZHL46q',
    defaultMessage: 'View details',
  })

  const hideDetailsLabel = intl.formatMessage({
    id: 'dajZ_ENAf-R1kN8fx16N',
    defaultMessage: 'Hide details',
  })

  const baggageLabel = intl.formatMessage({
    id: 'uTNi-Of34XV1T7_DrVtl',
    defaultMessage: 'Baggage',
  })

  const seatsMealsLabel = intl.formatMessage({
    id: 'ln37QEWNdzClbPPS4-em',
    defaultMessage: 'Seats, meals and more',
  })

  const showMoreInclusionsLabel = intl.formatMessage({
    id: 'apVzhMG-RTy8a3CPNKym',
    defaultMessage: 'Show more',
  })

  const showLessInclusionsLabel = intl.formatMessage({
    id: 'aRvIRv0RQmXlb7F1EqHi',
    defaultMessage: 'Show less',
  })

  const totalInclusions = getCharterCabinFeatureCount(baseFareClass, 'inclusions')
  const inclusionsHaveMore = totalInclusions > COLLAPSED_INCLUSIONS_COUNT

  const handleFareDetailsToggleClick = useCallback((event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onToggleFareDetails()
  }, [onToggleFareDetails])

  // Without stopPropagation the click bubbles to the parent date row, which interprets it as
  // a tap on the row card and collapses the expanded fare details — the toggle would appear
  // to "close" the card instead of expanding the inclusions list.
  const handleExtraFeaturesToggleClick = useCallback((event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onToggleExtraFeatures()
  }, [onToggleExtraFeatures])

  // Hide the toggle while this card's pricing is still loading. If we let the user expand mid-fetch,
  // the column lock captures the (smaller) loading-state height and the price content visually
  // overflows onto the fare-details section once it renders.
  const canShowFareDetailsLink = Boolean(checkIn?.trim() && checkOut?.trim()) && !bundleData.fetching

  const fareDetailsToggleLabel = fareDetailsExpanded ? hideDetailsLabel : showDetailsLabel

  const isFareSelectable = (oneWayBundle || bundleData.available) && !bundleData.fetching && (
    isRewardFare ?
      rewardCellPrice?.pointsTotal != null && rewardCellPrice.cashTotal != null :
      cashCellPrice?.cashTotal != null
  )

  const handlePick = useCallback(() => {
    if (!isFareSelectable) {
      return
    }
    let price = priceMode === 'perPerson' ? bundleData.pricePerPerson : bundleData.totalPrice
    let memberPrice = priceMode === 'perPerson'
      ? bundleData.memberPricePerPerson
      : bundleData.memberTotalPrice
    if (isRewardFare && rewardCellPrice?.cashTotal != null) {
      price = rewardCellPrice.cashTotal
      memberPrice = undefined
    }
    onPickFare(optionFareClass, {
      ...tripOption,
      price,
      memberPrice: memberPrice ?? undefined,
      available: isFareSelectable,
      seatsLeft: bundleData.seatsLeft,
    })
  }, [
    isFareSelectable,
    bundleData.memberPricePerPerson,
    bundleData.memberTotalPrice,
    bundleData.pricePerPerson,
    bundleData.seatsLeft,
    bundleData.totalPrice,
    isRewardFare,
    onPickFare,
    optionFareClass,
    priceMode,
    rewardCellPrice,
    tripOption,
  ])

  const unitSuffix = priceMode === 'perPerson' ? perPersonSuffix : totalSuffix

  const cabinTitleRow = isRewardFare
    ? (
        <Group direction="vertical" gap={4}>
          <Heading variant="heading6">{cabinLabel}</Heading>
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <ClassicRewardsSealIcon size="XS" colour="highlight-primary-dark" />
            <Caption variant="medium" weight="bold" colour="highlight-primary-dark">
              {rewardsLabel}
            </Caption>
          </Group>
        </Group>
      )
    : <Heading variant="heading6">{cabinLabel}</Heading>

  let fareActionButton
  if (isSelectedFare) {
    fareActionButton = onUnselectSelectedFare
      ? (
          <TextButton
            kind="primary"
            fit="flex"
            onClick={onUnselectSelectedFare}
            aria-label={unselectSelectedFareAriaLabel}
          >
            {selectedFareButtonLabel}
          </TextButton>
        )
      : (
          <TextButton
            kind="primary"
            nonInteractable
            tabIndex={-1}
            fit="flex"
          >
            {selectedFareButtonLabel}
          </TextButton>
        )
  } else {
    if (rewardLockedForLogin) {
      fareActionButton = (
        <TextButton
          kind="secondary"
          onClick={() => showModal(<AccountAccessModal />)}
          fit="flex"
        >
          {loginToViewLabel}
        </TextButton>
      )
    } else if (rewardLockedForEnroll) {
      fareActionButton = (
        <TextButton
          kind="secondary"
          onClick={() => showModal(<LoyaltyJoinModal
            onAfterClose={() => {}}
            primaryActionCta={enrollLabel}
          />)}
          fit="flex"
        >
          {enrollLabel}
        </TextButton>
      )
    } else if (isFareSelectable) {
      fareActionButton = (
        <TextButton
          kind="secondary"
          onClick={handlePick}
          fit="flex"
        >
          {selectFareLabel}
        </TextButton>
      )
    }
  }

  const cardPadding = isRewardFare ? '28 16 16 16' : 16

  /**
   * When fare details are open, the carousel row height can grow (equal-height columns). `min-height` alone still
   * lets this flex child absorb extra space (`flex: 1 1 auto`), growing the leading spacer and shifting CTAs. Lock
   * height and `flex: 0 0 auto` so the price / CTA stack stays pixel-stable while details mount below.
   */
  const cardMainColumnStyle = useMemo((): CSSProperties => {
    if (!fareDetailsExpanded || collapsedMainColumnHeightPx == null) {
      return {}
    }
    const h = collapsedMainColumnHeightPx
    return {
      flex: '0 0 auto',
      height: h,
      minHeight: h,
      maxHeight: h,
    }
  }, [fareDetailsExpanded, collapsedMainColumnHeightPx])

  // Pre-compute reward preview pricing (locked users)
  const rewardPreviewRate = getRewardPreviewRate({
    cabin: toBaseFareClass(optionFareClass),
    oneWayBundle,
    departureDate: tripOption.departDate,
  })
  const rewardPreviewShowMember = rewardPreviewRate ? (luxPlusEnabled && (cabinAwareHotelMemberPrice !== undefined || bundleData.hotelMemberPrice !== undefined)) : false
  const rewardPreviewHotelBase = rewardPreviewRate ? ((rewardPreviewShowMember ? (cabinAwareHotelMemberPrice ?? bundleData.hotelMemberPrice) : (cabinAwareHotelPrice ?? bundleData.hotelPrice)) ?? 0) : 0
  const rewardPreviewPpDivisor = priceMode === 'perPerson' && seatEligiblePassengers > 0 ? seatEligiblePassengers : 1
  const rewardPreviewPoints = rewardPreviewRate ? (rewardPreviewRate.pointsPerPerson * seatEligiblePassengers) / rewardPreviewPpDivisor : 0
  const rewardPreviewCash = rewardPreviewRate ? (rewardPreviewHotelBase + rewardPreviewRate.cashPerPerson * seatEligiblePassengers) / rewardPreviewPpDivisor : 0
  const rewardPreviewAvailable = oneWayBundle && rewardLockedForLogin
    ? rewardCellPrice?.cashTotal != null && seatEligiblePassengers > 0
    : bundleData.available
  const rewardPreviewColour = rewardPreviewShowMember ? 'lux-plus' : 'neutral-one'

  // Pre-compute unlocked reward pricing
  const showMemberReward = luxPlusEnabled && rewardCellPrice?.cashMemberTotal != null
  const rawRewardCash = showMemberReward ? rewardCellPrice?.cashMemberTotal : rewardCellPrice?.cashTotal
  const rewardColour = showMemberReward ? 'lux-plus' : 'neutral-one'
  const rewardPerPersonDivisor = priceMode === 'perPerson' && seatEligiblePassengers > 0 ? seatEligiblePassengers : 1

  return (
    <CardWrap>
      {isRewardFare && <CharterRewardGreatValueBadge />}
      <CardPane padding={cardPadding} background="neutral-eight" $selected={isSelectedFare}>
        <CardHeaderSlot>
          <VerticalSpacer gap={12}>
            {cabinTitleRow}
            {oneWayBundle && !bundleData.fetching && isRewardFare && baseFareClass === FlightsClassTypes.ECONOMY &&
              rewardCellPrice?.cashTotal != null && seatEligiblePassengers > 0 && (!memberId || rewardCellPrice.pointsTotal != null) && (
              <OneWayFlightSaleLabel departureDate={tripOption.departDate} />
            )}
            {bundleData.fetching && <Caption variant="medium" colour="neutral-three">{loadingLabel}</Caption>}
          </VerticalSpacer>
        </CardHeaderSlot>
        <CardMainColumn ref={cardMainColumnRef} style={cardMainColumnStyle}>
          <FareCardMainLeadingSpacer aria-hidden />
          <PreFareDetailsBlock>
            <VerticalSpacer gap={12}>
              {!bundleData.fetching && rewardLocked && rewardPreviewAvailable && !rewardPreviewRate && (
                <BodyText variant="medium" colour="neutral-two">{loginToViewLabel}</BodyText>
              )}
              {!bundleData.fetching && rewardLocked && rewardPreviewAvailable && rewardPreviewRate && (
                <VerticalSpacer gap={4}>
                  <PriceRowCaption>{fromLabel}</PriceRowCaption>
                  <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
                    <Caption variant="large" weight="bold" colour={rewardPreviewColour}>
                      <FormattedMessage
                        id="NWJKbOzkq6Wsvund56uC"
                        defaultMessage="{points} + {cash}"
                        
                        values={{
                          points: <FormatLoyaltyPoints abbreviated points={rewardPreviewPoints} />,
                          cash: <FormatCurrency value={rewardPreviewCash} format="roundedDollar" />,
                        }}
                      />
                    </Caption>
                    <Caption variant="medium" colour={rewardPreviewShowMember ? 'lux-plus' : 'neutral-three'} weight="normal">
                      {unitSuffix}
                    </Caption>
                  </Group>
                </VerticalSpacer>
              )}
              {!bundleData.fetching && isRewardFare && !rewardLocked && rewardCellPrice?.pointsTotal != null && rewardCellPrice.cashTotal != null && (
                <VerticalSpacer gap={4}>
                  <PriceRowCaption>{fromLabel}</PriceRowCaption>
                  <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
                    <Caption variant="large" weight="bold" colour={rewardColour}>
                      <FormattedMessage
                        id="NWJKbOzkq6Wsvund56uC"
                        defaultMessage="{points} + {cash}"
                        
                        values={{
                          points: <FormatLoyaltyPoints abbreviated points={rewardCellPrice.pointsTotal / rewardPerPersonDivisor} />,
                          cash: <FormatCurrency value={(rawRewardCash ?? 0) / rewardPerPersonDivisor} format="roundedDollar" />,
                        }}
                      />
                    </Caption>
                    <Caption variant="medium" colour={showMemberReward ? 'lux-plus' : 'neutral-three'} weight="normal">
                      {unitSuffix}
                    </Caption>
                  </Group>
                </VerticalSpacer>
              )}
              {!bundleData.fetching && !isRewardFare && cashCellPrice?.cashTotal != null && displayAmount > 0 && (
                <Group direction="horizontal" gap={4} verticalAlign="baseline">
                  <PriceRowCaption>{fromLabel}</PriceRowCaption>
                  <Caption variant="large" weight="bold" colour={showReturnFareFree ? 'lux-plus' : 'neutral-one'}>
                    {showReturnFareFree && <InlineIcon Icon={SolidFireIcon} colour="lux-plus" />}
                    <FormatCurrency value={displayAmount} format="roundedDollar" />
                  </Caption>
                  <Caption variant="medium" colour="neutral-three" weight="normal">
                    {unitSuffix}
                  </Caption>
                </Group>
              )}
              {!bundleData.fetching && isRewardFare && !rewardLocked && (rewardCellPrice?.pointsTotal == null || rewardCellPrice.cashTotal == null) && (
                <BodyText variant="medium" colour="neutral-three">
                  {unavailableLabel}
                </BodyText>
              )}
              {!bundleData.fetching && isRewardFare && rewardLocked && !rewardPreviewAvailable && (
                <BodyText variant="medium" colour="neutral-three">
                  {unavailableLabel}
                </BodyText>
              )}
              {!bundleData.fetching && !isRewardFare && (cashCellPrice?.cashTotal == null || displayAmount <= 0) && (
                <BodyText variant="medium" colour="neutral-three">
                  {unavailableLabel}
                </BodyText>
              )}
              {!bundleData.fetching && luxPlusEnabled && !!cashCellPrice?.promotionSeatsLeft && (
                <Caption variant="medium" weight="bold" colour="urgency">
                  <FormattedMessage
                    id="23YWa0_2ZjY7SjWiY2Ak"
                    defaultMessage="{count, plural, one {# seat left at this price} other {# seats left at this price}}"
                    
                    values={{ count: cashCellPrice.promotionSeatsLeft }}
                  />
                </Caption>
              )}
              {fareActionButton}
              {canShowFareDetailsLink && (
                <FareDetailsToggleSlot>
                  <TextButton
                    kind="tertiary"
                    variant="default"
                    size="small"
                    fit="auto"
                    horizontalOutdent="start"
                    onClick={handleFareDetailsToggleClick}
                  >
                    {fareDetailsToggleLabel}
                  </TextButton>
                </FareDetailsToggleSlot>
              )}
            </VerticalSpacer>
          </PreFareDetailsBlock>
        </CardMainColumn>
        {fareDetailsExpanded && (
          <>
            <Divider kind="primary" />
            {/* Hardcoded baggage + inclusions lists shared with the cabin selection modal, so
                the same copy/icons render across surfaces. Independent of seat availability. */}
            <div data-testid="maldives-fare-details-region">
              <VerticalSpacer gap={16}>
                <VerticalSpacer gap={8}>
                  <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
                  <CharterCabinFeatureList fareClass={baseFareClass} section="baggage" />
                </VerticalSpacer>
                <VerticalSpacer gap={8}>
                  <BodyText variant="small" weight="bold">{seatsMealsLabel}</BodyText>
                  <CharterCabinFeatureList
                    fareClass={baseFareClass}
                    section="inclusions"
                    maxItems={extraFeaturesExpanded ? undefined : COLLAPSED_INCLUSIONS_COUNT}
                  />
                  {inclusionsHaveMore && (
                    <TextButton
                      kind="tertiary"
                      variant="default"
                      size="small"
                      fit="auto"
                      horizontalOutdent="start"
                      onClick={handleExtraFeaturesToggleClick}
                    >
                      {extraFeaturesExpanded ? showLessInclusionsLabel : showMoreInclusionsLabel}
                    </TextButton>
                  )}
                </VerticalSpacer>
              </VerticalSpacer>
            </div>
          </>
        )}
      </CardPane>
    </CardWrap>
  )
}

export default MaldivesCharterFareCarouselOption
